import React from 'react'
import { NextPage } from 'next'
import { wrapper } from 'src/redux'
import { Box, Heading, Text } from '@chakra-ui/react'
import { makeId } from 'src/utils/random'
import { theme } from 'src/themes'
import { useRouter } from 'next/router'
import Breadcrumbs from 'src/components/Breadcrumbs'
import { Head, LinkRel, MetaTags } from 'src/components/common'
import policyCookieData from 'src/containers/data/policies/cookie.json'
import policyPrivacyData from 'src/containers/data/policies/privacy.json'
import policyTermsConditionsData from 'src/containers/data/policies/terms-and-conditions.json'

interface Props {
  slug: string
  policiesContent: Policies.Content
}

const PolicyPage: NextPage<Props> = ({ slug, policiesContent }) => {
  const router = useRouter()

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Policy',
      title: 'Policy',
    },
    {
      page: policiesContent.title.replace('policy', ''),
      href: router.asPath,
      title: policiesContent.title,
    },
  ]

  return <>
    <Head title={policiesContent.title} />
    <MetaTags meta={[{ name: "description", content: policiesContent.description }]} />
    <LinkRel rel="canonical" href={`https://www.carplanner.com/policies/${slug}`} />

    <Breadcrumbs items={crumbs} />
    <Box maxW="7xl" py="l" mx="auto">
      <Heading as="h1" fontSize="2xl" fontFamily="Montserrat-SemiBold" textAlign="center" mb="l">
        {policiesContent.title}
      </Heading>

      <Box
        fontFamily="Montserrat-Regular"
        fontSize="1rem"
        textAlign="left"
        className="policies-page"
      >
        {policiesContent.content.length && policiesContent.content.map((content, idx) =>
          <Box
            key={makeId(idx)}
            bg="baseWhite"
            p="l"
            my="s"
            boxShadow={theme.shadows.cards}
            textAlign="left"
          >
            {content.title && <Text fontFamily="Montserrat-SemiBold" color="baseNeutral.700">
              {content.title}
            </Text>
            }
            <Box
              fontFamily="Montserrat-Medium"
              className="faq-answer"
              color="baseNeutral.700"
              dangerouslySetInnerHTML={{ __html: content.text }}
            />
          </Box>
        )}
      </Box>
    </Box>
  </>
}

export async function getStaticPaths() {
  const paths = [
    'cookie',
    'privacy',
    'terms-and-conditions'
  ].map(slug => ({
    params: { slug },
  }))
  return { paths, fallback: false }
}

export const getStaticProps = wrapper.getStaticProps(() => async ({ params }) => {
  let { slug } = params
  let policyProps = {};

  slug = Array.isArray(slug) ? slug[0] : slug

  switch(slug) {
    case 'cookie':
      policyProps = policyCookieData.data;
      break;
    case 'privacy':
      policyProps = policyPrivacyData.data;
      break;
    case 'terms-and-conditions':
      policyProps = policyTermsConditionsData.data;
      break;
  }

  return { props: { policiesContent: policyProps, slug } }
})

export default PolicyPage
